import { ActivityTimeline, DetailList, formatDateTime, ListView, RecordDrawer, useCursorList } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { ConfirmMorph } from "@majitap/ui/arc/confirm-morph/confirm-morph";
import { DatePicker } from "@majitap/ui/arc/date-picker/date-picker";
import { Select } from "@majitap/ui/arc/select/select";
import { useQuery } from "@tanstack/react-query";
import { useActivity } from "../../activity";
import { api, errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import {
  answerText,
  closesIn,
  END_REASON,
  fleetActions,
  ownerQuery,
  type Shift,
  shiftLength,
  shiftQuery,
  useRefreshFleet,
} from "../../fleet";
import { RouterLink } from "../../router-link";
import { OwnerFilter } from "./filters";
import styles from "./fleet.module.css";
import { OpenShiftDialog } from "./open-shift";
import type { FleetCan, FleetSearch } from "./page";

const SHOW = { open: "On shift now", closed: "Ended", all: "All shifts" } as const;

/** "2026-10-18" as a picker's local Date, and back. */
const dayDate = (day: string) => {
  const [y, m, d] = day.split("-");
  return new Date(`${y}-${m}-${d}T00:00:00`);
};
const dayOf = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/**
 * Shifts (`Console / Fleet / On shift` in the design): who's on shift now, the shifts that ended,
 * or all, newest first, by the day they started. `?shift=` opens one beside the list,
 * with the checklist answered to open it. Drivers open and close shifts in the driver app; a shift
 * closes by itself 24 hours after it starts; staff open one for a driver who can't in the app
 * (P8-59) and close one a driver forgot to end.
 */
export function ShiftsView({
  search,
  set,
  can,
  adding,
  onAdded,
}: {
  search: FleetSearch;
  set: (patch: Partial<FleetSearch>, replace?: boolean) => void;
  can: FleetCan;
  adding: boolean;
  onAdded: () => void;
}) {
  const format = useCompanyFormat();
  const show = search.shifts ?? "open";
  const date = show === "open" ? undefined : search.date;
  const list = useCursorList({
    queryKey: ["fleet", "shifts", "list", show, date ?? "", search.owner ?? ""],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/fleet/shifts", {
        params: { query: { status: show, date, ...ownerQuery(search.owner), cursor, limit: 25 } },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  const refresh = useRefreshFleet();
  const now = Date.now();

  return (
    <>
      <ListView
        caption={SHOW[show]}
        itemName={{ one: "shift", other: "shifts" }}
        rows={list.rows}
        rowKey={(s) => s.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={Boolean(date || search.owner)}
        onResetFilters={() => set({ date: undefined, owner: undefined })}
        empty={
          show === "open"
            ? {
                title: "Nobody is on shift",
                description: "Drivers start a shift in the driver app before their first delivery.",
              }
            : { title: "No shifts yet", description: "Shifts show here once drivers start them." }
        }
        toolbar={
          <div className={styles.toolbarEnd}>
            <Select
              label="Show"
              value={show}
              onValueChange={(v) =>
                set({ shifts: v === "open" ? undefined : (v as "closed" | "all"), date: undefined })
              }
              options={Object.entries(SHOW).map(([value, label]) => ({ value, label }))}
            />
            {show === "open" ? null : (
              <DatePicker
                label="Started on"
                value={date ? dayDate(date) : undefined}
                onChange={(d) => set({ date: d ? dayOf(d) : undefined })}
                showToday
              />
            )}
            <OwnerFilter value={search.owner} onChange={(owner) => set({ owner })} partners={can.partners} />
          </div>
        }
        columns={[
          {
            key: "driver",
            label: "Driver",
            render: (s) => (
              <button type="button" className={styles.link} onClick={() => set({ shift: s.id })}>
                {s.driver.name}
              </button>
            ),
          },
          { key: "truck", label: "Truck", render: (s) => s.truck?.plate_number ?? "—" },
          { key: "assistant", label: "Assistant", render: (s) => s.assistant?.name ?? "—" },
          { key: "started_at", label: "Started", render: (s) => formatDateTime(s.started_at, format) },
          ...(show === "open"
            ? [
                { key: "length", label: "On shift for", render: (s: Shift) => shiftLength(s.started_at, now) },
                { key: "closes", label: "Closes in", render: (s: Shift) => closesIn(s.expires_at, now) },
              ]
            : [
                {
                  key: "ended_at",
                  label: "Ended",
                  render: (s: Shift) =>
                    s.ended_at ? (
                      <span className={styles.stack}>
                        <span>{formatDateTime(s.ended_at, format)}</span>
                        {s.end_reason ? <span className={styles.muted}>{END_REASON[s.end_reason]}</span> : null}
                      </span>
                    ) : Date.parse(s.expires_at) <= now ? (
                      // past 24 hours: over, the worker closes it shortly
                      <span className={styles.muted}>{END_REASON.expired}</span>
                    ) : (
                      <Badge tone="success">On shift</Badge>
                    ),
                },
              ]),
          {
            key: "actions",
            label: "",
            // room for the question face, which is wider than the button
            width: "17rem",
            render: (s) =>
              can.manage && show === "open" ? (
                <div className={styles.end}>
                  <ConfirmMorph
                    label="Close shift"
                    prompt="Close it?"
                    confirmLabel="Close shift"
                    cancelLabel="Keep open"
                    pendingLabel="Closing"
                    doneLabel="Closed"
                    onConfirm={() => fleetActions.closeShift(s.id)}
                    onStateChange={(state) => {
                      if (state === "done") void refresh();
                    }}
                    resultTimeout={1500}
                  />
                </div>
              ) : null,
          },
        ]}
      />
      <p className={styles.hint}>
        Drivers open and close shifts in the driver app, and a shift closes by itself 24 hours after it starts. Open one
        here for a driver who can't in the app, and close one only when a driver forgot to end it.
        {can.manage ? (
          <>
            {" "}
            What drivers answer to open a shift is in{" "}
            <RouterLink href="/settings/shift-checklist">Settings › Shift checklist</RouterLink>.
          </>
        ) : null}
      </p>
      <ShiftDrawer id={search.shift} onClose={() => set({ shift: undefined })} />
      {adding ? <OpenShiftDialog onClose={onAdded} /> : null}
    </>
  );
}

/** One shift beside the list: the crew, when it ran, and the checklist answered to open it. */
function ShiftDrawer({ id, onClose }: { id: string | undefined; onClose: () => void }) {
  const format = useCompanyFormat();
  const shift = useQuery({ ...shiftQuery(id ?? ""), enabled: Boolean(id) });
  const activity = useActivity("driver_shift", id ?? null);
  const s = shift.data;
  const ended = s?.ended_at
    ? formatDateTime(s.ended_at, format)
    : s && Date.parse(s.expires_at) <= Date.now()
      ? "After 24 hours"
      : null;
  return (
    <RecordDrawer
      open={Boolean(id)}
      onOpenChange={(o) => !o && onClose()}
      title={s ? `${s.driver.name}'s shift` : "Shift"}
      description={s ? formatDateTime(s.started_at, format) : undefined}
    >
      {shift.error ? (
        <Alert tone="danger" title="Couldn't load the shift">
          {messageFor(errorOf(shift.error))}
        </Alert>
      ) : null}
      {s ? (
        <div className={styles.drawer}>
          <DetailList
            items={[
              { label: "Driver", value: s.driver.name },
              { label: "Truck", value: s.truck?.plate_number ?? null },
              { label: "Assistant", value: s.assistant?.name ?? null },
              { label: "Started", value: formatDateTime(s.started_at, format) },
              { label: "Opened by", value: s.opened_by ? `${s.opened_by.name}, in the console` : null },
              ended
                ? { label: "Ended", value: ended }
                : { label: "Closes by itself", value: formatDateTime(s.expires_at, format) },
              { label: "Why it ended", value: s.end_reason ? END_REASON[s.end_reason] : null },
            ]}
          />
          <section className={styles.checklist} aria-label="Checklist">
            <h3 className={styles.sectionTitle}>Checklist</h3>
            {s.checklist.length === 0 ? (
              <p className={styles.muted}>No checklist was asked for when this shift started.</p>
            ) : (
              <DetailList
                items={s.checklist.map((a) => ({
                  label: a.label,
                  value: a.photo_url ? (
                    <a href={a.photo_url} target="_blank" rel="noreferrer" className={styles.photoLink}>
                      <img src={a.photo_url} alt={a.label} className={styles.photo} />
                    </a>
                  ) : (
                    answerText(a)
                  ),
                }))}
              />
            )}
          </section>
          {activity.data ? <ActivityTimeline entries={activity.data} timeZone={format.timezone} /> : null}
        </div>
      ) : null}
    </RecordDrawer>
  );
}
